/**
 * Typed client for the dashboard's /api/support/* proxy. Every call
 * goes through `api<T>()` so it picks up token refresh, error mapping
 * to ApiError, and JWT attachment.
 */
import { api } from "../../lib/api.js";

import type {
  AdminPaginatedEnvelope,
  AdminTicket,
  AdminTicketComment,
  AdminTicketDetail,
  AdminTicketSla,
  ConversationDetail,
  ConversationListEnvelope,
  EscalateRequest,
  EscalateResponse,
  PaginatedEnvelope,
  SingleEnvelope,
  SupportAssignableUser,
  SupportAuditLog,
  SupportContact,
  SupportHealth,
  SupportKbArticle,
  SupportKbCategory,
  SupportOrganization,
  SupportPortalBranding,
  SupportPortalFaq,
  SupportPriority,
  SupportReportAgent,
  SupportReportPoint,
  SupportReportSummary,
  SupportSlaPolicy,
  SupportStatus,
  SupportTicket,
  SupportTicketAttachment,
  SupportTicketComment,
  SupportUser,
} from "./types.js";

export interface ListConversationsParams {
  q?: string;
  organization_id?: string;
  user_id?: string;
  persona?: string;
  status?: string;
  flagged?: string;
  from?: string;
  to?: string;
  page?: string;
  per_page?: string;
  [k: string]: string | undefined;
}

export interface ListTicketsParams {
  q?: string;
  status?: string;
  assignee_id?: string;
  priority?: string;
  organization_id?: string;
  page?: string;
  per_page?: string;
  /** Allow-listed by Rails: created_at, updated_at, due_date, title,
   * key, priority_id. */
  sort?: string;
  /** "asc" | "desc"; defaults to "desc" on the server. */
  dir?: string;
  [k: string]: string | undefined;
}

/**
 * Filter params accepted by the admin index (`GET /api/admin/tickets`).
 * Status/priority are *slug-based* (not id-based) so the chips on the
 * staff list page must surface `s.slug` as the option value. The rest
 * mirror the legacy support index.
 */
export interface ListAdminTicketsParams {
  q?: string;
  /** Status slug (e.g. "open", "in_progress"). */
  status?: string;
  /** Priority slug (e.g. "high", "urgent"). */
  priority?: string;
  assignee_id?: string;
  /** Customer filter — the admin index aliases this onto `organization_id`. */
  organization_id?: string;
  page?: string;
  per_page?: string;
  sort?: string;
  dir?: string;
  [k: string]: string | undefined;
}

function qs(params: Record<string, string | undefined>): string {
  const u = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v) u.set(k, v);
  const s = u.toString();
  return s ? `?${s}` : "";
}

/**
 * Multipart upload helper. Bypasses the JSON `api()` wrapper because
 * it needs a FormData body and the browser-set boundary content-type.
 * Used by every "attach files to X" call in the support module so the
 * token-store + env-url plumbing only lives in one place.
 */
async function uploadFiles(
  path: string,
  files: File[],
): Promise<{ data: SupportTicketAttachment[] }> {
  const fd = new FormData();
  for (const f of files) fd.append("files[]", f, f.name);
  const { tokenStore } = await import("../../lib/tokenStore.js");
  const { env } = await import("../../lib/env.js");
  const token = tokenStore.getAccess();
  const res = await fetch(`${env.apiUrl}${path}`, {
    method: "POST",
    body: fd,
    credentials: "include",
    headers: token ? { authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    let body: { error?: string; code?: string } = {};
    try {
      body = (await res.json()) as typeof body;
    } catch {
      /* non-JSON */
    }
    throw new Error(body.error ?? `Upload failed (HTTP ${res.status})`);
  }
  return (await res.json()) as { data: SupportTicketAttachment[] };
}

export const supportApi = {
  /* ── Backend health probe ──────────────────────────────────────── */
  getHealth: () => api<SupportHealth>("/api/support/health"),

  /* ── Tickets ───────────────────────────────────────────────────── */
  listTickets: (p: ListTicketsParams) =>
    api<PaginatedEnvelope<SupportTicket>>(`/api/support/tickets${qs(p)}`),

  /**
   * Admin ticket index — the new endpoint added by Task 12 of the
   * 2026-05-13 ticket-system-completion plan. Returns the richer admin
   * row shape (`requester_name`, `assignee_name`, `sla_status`,
   * `last_activity_at`) and a flat `meta: { page, per_page, total }`.
   * The staff list page uses this; `listTickets` above is retained for
   * any caller that still wants the legacy `/api/support/tickets`
   * shape (e.g. organization detail "recent tickets" sub-tables).
   */
  listAdminTickets: (p: ListAdminTicketsParams) =>
    api<AdminPaginatedEnvelope<AdminTicket>>(`/api/admin/tickets${qs(p)}`),

  /**
   * Single-ticket detail through the admin namespace. Returns the
   * richer admin row shape extended with `ticket_sla` (per-ticket SLA
   * override timestamps) and `watchers` (subscribed teammates). The
   * `<SlaPanel>` and `<WatcherMenu>` mounted on `TicketDetailPage`
   * key off these two fields.
   */
  getAdminTicket: (id: string | number) =>
    api<{ data: AdminTicketDetail }>(`/api/admin/tickets/${id}`),

  /**
   * Per-ticket priority change via the admin namespace. Distinct from
   * `updateTicket({ priority_id })` above — that hits the legacy
   * `/api/support/tickets/:id` PATCH which mutates a wider set of
   * descriptive fields. This dedicated endpoint routes through Rails
   * `Tickets::UpdatePriority` so history + audit + customer activity
   * notifications fire from one well-known place.
   */
  updateAdminTicketPriority: (
    id: string | number,
    body: { priority_id: number },
  ) =>
    api<{ data: { id: number; priority: { id: number; name: string; slug: string } } }>(
      `/api/admin/tickets/${id}/priority`,
      { method: "PATCH", body },
    ),

  /**
   * Per-ticket SLA override. `response_due_at` / `resolution_due_at`
   * are ISO-8601 strings (or `null` to clear an override).
   */
  updateAdminTicketSla: (
    id: string | number,
    body: { response_due_at: string | null; resolution_due_at: string | null },
  ) =>
    api<{ data: AdminTicketSla & { ticket_id: number } }>(
      `/api/admin/tickets/${id}/sla`,
      { method: "PATCH", body: { sla: body } },
    ),

  /** Add `user_id` to the ticket's watcher list. Idempotent — Rails
   * `Tickets::AddWatcher` no-ops if the user is already subscribed. */
  addAdminTicketWatcher: (id: string | number, body: { user_id: number }) =>
    api<{ data: { ticket_id: number; user_id: number } }>(
      `/api/admin/tickets/${id}/watchers`,
      { method: "POST", body },
    ),

  /** Remove `userId` from the ticket's watcher list. Idempotent. */
  removeAdminTicketWatcher: (
    id: string | number,
    userId: string | number,
  ) =>
    api<undefined>(`/api/admin/tickets/${id}/watchers/${userId}`, {
      method: "DELETE",
    }),

  /**
   * Staff comment POST through the admin namespace. Honours
   * `is_internal` (the customer-facing endpoint hard-codes it to
   * false). Returns the new row in the admin serializer shape
   * (`AdminTicketComment`).
   */
  postAdminTicketComment: (
    id: string | number,
    body: { body: string; is_internal: boolean },
  ) =>
    api<{ data: AdminTicketComment }>(`/api/admin/tickets/${id}/comments`, {
      method: "POST",
      body: { comment: body },
    }),

  /** Bulk soft-delete (discard) — staff only. */
  bulkDestroyTickets: (ids: number[]) =>
    api<{ data: { destroyed_ids: number[]; count: number } }>(
      "/api/support/tickets/bulk_destroy",
      { method: "POST", body: { ids } },
    ),

  /** Bulk transition every selected ticket to one status — staff only. */
  bulkTransitionTickets: (ids: number[], ticketStatusId: number) =>
    api<{ data: { updated_ids: number[]; count: number; ticket_status: { id: number; name: string } } }>(
      "/api/support/tickets/bulk_transition",
      { method: "POST", body: { ids, ticket_status_id: ticketStatusId } },
    ),

  /** Staff create-on-behalf-of: server allows organization_id +
   * reporter_id from staff actors; CreateTicket validates linkage. */
  createTicket: (body: {
    title: string;
    description?: string;
    organization_id: number;
    reporter_id?: number;
    priority_id?: number;
  }) =>
    api<SingleEnvelope<SupportTicket>>("/api/support/tickets", {
      method: "POST",
      body: { ticket: body },
    }),

  getTicket: (id: string | number) =>
    api<SingleEnvelope<SupportTicket>>(`/api/support/tickets/${id}`),

  listComments: (id: string | number) =>
    api<{ data: SupportTicketComment[] }>(`/api/support/tickets/${id}/comments`),

  postComment: (id: string | number, body: { body: string; internal: boolean }) =>
    api<SingleEnvelope<SupportTicketComment>>(
      `/api/support/tickets/${id}/comments`,
      {
        method: "POST",
        body: { ticket_comment: body },
      },
    ),

  transition: (
    id: string | number,
    body: { ticket_status_id: number; comment?: string },
  ) =>
    api<SingleEnvelope<SupportTicket>>(`/api/support/tickets/${id}/transition`, {
      method: "POST",
      body,
    }),

  assign: (id: string | number, body: { assignee_id: number | null }) =>
    api<SingleEnvelope<SupportTicket>>(`/api/support/tickets/${id}/assign`, {
      method: "POST",
      body,
    }),

  /** Partial update — used for "set priority" and any other
   * descriptive-field tweak the dashboard's Details panel adds
   * later. Status changes go through `transition`, assignee through
   * `assign` — those have dedicated endpoints because they emit
   * history entries + customer activity emails. */
  updateTicket: (
    id: string | number,
    attrs: Partial<{
      priority_id: number;
      assignee_id: number;
      ticket_status_id: number;
      title: string;
      description: string;
    }>,
  ) =>
    api<SingleEnvelope<SupportTicket>>(`/api/support/tickets/${id}`, {
      method: "PATCH",
      body: { ticket: attrs },
    }),

  /* ── Attachments (binary upload via multipart proxy) ──────────────── */
  listAttachments: (id: string | number) =>
    api<{ data: SupportTicketAttachment[] }>(`/api/support/tickets/${id}/attachments`),

  /** Multipart upload bypasses the JSON `api()` wrapper because it
   * needs a FormData body and the browser-set boundary content-type.
   * Auth header is attached manually from the token store. */
  uploadAttachments: async (
    id: string | number,
    files: File[],
  ): Promise<{ data: SupportTicketAttachment[] }> => {
    return uploadFiles(`/api/support/tickets/${id}/attachments`, files);
  },

  /** Files attached to a *specific comment* (used by the rich-text
   * reply box on the ticket detail page). The customer portal then
   * shows them inline alongside the comment body. */
  uploadCommentAttachments: async (
    ticketId: string | number,
    commentId: string | number,
    files: File[],
  ): Promise<{ data: SupportTicketAttachment[] }> => {
    return uploadFiles(
      `/api/support/tickets/${ticketId}/comments/${commentId}/attachments`,
      files,
    );
  },

  /* ── Lookups ───────────────────────────────────────────────────── */
  listStatuses: () => api<{ data: SupportStatus[] }>("/api/support/statuses"),
  listPriorities: () => api<{ data: SupportPriority[] }>("/api/support/priorities"),

  listAssignableUsers: (q?: string) =>
    api<{ data: SupportAssignableUser[] }>(
      `/api/support/users/assignable${qs({ q })}`,
    ),

  /* ── Organisations + contacts ──────────────────────────────────── */
  listOrganizations: (p: { q?: string; page?: string; per_page?: string } = {}) =>
    api<PaginatedEnvelope<SupportOrganization>>(
      `/api/support/organizations${qs(p)}`,
    ),

  getOrganization: (id: string | number) =>
    api<SingleEnvelope<SupportOrganization>>(`/api/support/organizations/${id}`),

  listOrgContacts: (id: string | number, q?: string) =>
    api<{ data: SupportContact[] }>(
      `/api/support/organizations/${id}/contacts${qs({ q })}`,
    ),

  /**
   * Recent tickets for one organisation (org-detail "Recent tickets"
   * card). Proxied to the staff admin index, so the rows come back in
   * the richer `AdminTicket` shape (`key`, `status`, …) and are scoped to
   * the org by an explicit `where(organization_id:)` upstream — not the
   * customer Ransack index, whose org filter was silently ignored.
   * `status`, when passed, is a status *slug*.
   */
  listOrgTickets: (
    id: string | number,
    p: { status?: string; page?: string; per_page?: string } = {},
  ) =>
    api<AdminPaginatedEnvelope<AdminTicket>>(
      `/api/support/organizations/${id}/tickets${qs(p)}`,
    ),

  /* ── Knowledge base ────────────────────────────────────────────── */
  listKbArticles: (
    p: {
      q?: string;
      category_id?: string;
      status?: string;
      page?: string;
      per_page?: string;
    } = {},
  ) => api<PaginatedEnvelope<SupportKbArticle>>(`/api/support/knowledge${qs(p)}`),

  listKbCategories: () =>
    api<{ data: SupportKbCategory[] }>("/api/support/knowledge/categories"),

  /* ── Staff users ───────────────────────────────────────────────── */
  listUsers: (
    p: { q?: string; role?: string; page?: string; per_page?: string } = {},
  ) => api<PaginatedEnvelope<SupportUser>>(`/api/support/users${qs(p)}`),

  getUser: (id: string | number) =>
    api<SingleEnvelope<SupportUser>>(`/api/support/users/${id}`),

  inviteUser: (body: {
    email: string;
    first_name?: string;
    last_name?: string;
    role: string;
  }) =>
    api<SingleEnvelope<SupportUser>>("/api/support/users", {
      method: "POST",
      body: { user: body },
    }),

  updateUser: (id: string | number, body: Partial<SupportUser>) =>
    api<SingleEnvelope<SupportUser>>(`/api/support/users/${id}`, {
      method: "PATCH",
      body: { user: body },
    }),

  /* ── Workflows (statuses + priorities CRUD) ────────────────────── */
  createStatus: (body: { name: string; category: string; color?: string }) =>
    api<SingleEnvelope<SupportStatus>>("/api/support/statuses", {
      method: "POST",
      body: { ticket_status: body },
    }),

  updateStatus: (id: number, body: Partial<SupportStatus>) =>
    api<SingleEnvelope<SupportStatus>>(`/api/support/statuses/${id}`, {
      method: "PATCH",
      body: { ticket_status: body },
    }),

  deleteStatus: (id: number) =>
    api<{ ok: boolean }>(`/api/support/statuses/${id}`, { method: "DELETE" }),

  createPriority: (body: { name: string; color?: string }) =>
    api<SingleEnvelope<SupportPriority>>("/api/support/priorities", {
      method: "POST",
      body: { priority: body },
    }),

  updatePriority: (id: number, body: Partial<SupportPriority>) =>
    api<SingleEnvelope<SupportPriority>>(`/api/support/priorities/${id}`, {
      method: "PATCH",
      body: { priority: body },
    }),

  deletePriority: (id: number) =>
    api<{ ok: boolean }>(`/api/support/priorities/${id}`, { method: "DELETE" }),

  /* ── Audit log ─────────────────────────────────────────────────── */
  listAudit: (
    p: {
      q?: string;
      actor_id?: string;
      action?: string;
      page?: string;
      per_page?: string;
    } = {},
  ) => api<PaginatedEnvelope<SupportAuditLog>>(`/api/support/audit${qs(p)}`),

  /* ── Reports ───────────────────────────────────────────────────── */
  reportSummary: (range = "30d") =>
    api<SingleEnvelope<SupportReportSummary>>(
      `/api/support/reports/summary${qs({ range })}`,
    ),

  reportVolume: (range = "30d") =>
    api<{ data: SupportReportPoint[] }>(
      `/api/support/reports/volume${qs({ range })}`,
    ),

  reportSla: (range = "30d") =>
    api<{ data: SupportReportPoint[] }>(
      `/api/support/reports/sla${qs({ range })}`,
    ),

  reportAgents: (range = "30d") =>
    api<{ data: SupportReportAgent[] }>(
      `/api/support/reports/agents${qs({ range })}`,
    ),

  /* ── Settings ──────────────────────────────────────────────────── */
  listSlaPolicies: () =>
    api<{ data: SupportSlaPolicy[] }>("/api/support/sla-policies"),

  createSlaPolicy: (body: Omit<SupportSlaPolicy, "id">) =>
    api<SingleEnvelope<SupportSlaPolicy>>("/api/support/sla-policies", {
      method: "POST",
      body: { sla_policy: body },
    }),

  updateSlaPolicy: (id: number, body: Partial<SupportSlaPolicy>) =>
    api<SingleEnvelope<SupportSlaPolicy>>(`/api/support/sla-policies/${id}`, {
      method: "PATCH",
      body: { sla_policy: body },
    }),

  deleteSlaPolicy: (id: number) =>
    api<{ ok: boolean }>(`/api/support/sla-policies/${id}`, { method: "DELETE" }),

  /* ── Portal branding ───────────────────────────────────────────── */
  getPortalBranding: () =>
    api<SingleEnvelope<SupportPortalBranding>>("/api/support/portal-branding"),

  updatePortalBranding: (body: Partial<SupportPortalBranding>) =>
    api<SingleEnvelope<SupportPortalBranding>>("/api/support/portal-branding", {
      method: "PATCH",
      body: { portal_branding: body },
    }),

  listFaqs: () => api<{ data: SupportPortalFaq[] }>("/api/support/portal-branding/faqs"),

  createFaq: (body: Omit<SupportPortalFaq, "id">) =>
    api<SingleEnvelope<SupportPortalFaq>>("/api/support/portal-branding/faqs", {
      method: "POST",
      body: { faq: body },
    }),

  updateFaq: (id: number, body: Partial<SupportPortalFaq>) =>
    api<SingleEnvelope<SupportPortalFaq>>(`/api/support/portal-branding/faqs/${id}`, {
      method: "PATCH",
      body: { faq: body },
    }),

  deleteFaq: (id: number) =>
    api<{ ok: boolean }>(`/api/support/portal-branding/faqs/${id}`, {
      method: "DELETE",
    }),

  /* ── Otto conversation history ─────────────────────────────────── */
  listConversations: (p: ListConversationsParams = {}) =>
    api<ConversationListEnvelope>(`/api/support/conversations${qs(p)}`),

  getConversation: (id: string | number) =>
    api<SingleEnvelope<ConversationDetail>>(`/api/support/conversations/${id}`),

  flagConversation: (id: string | number, reason: string) =>
    api<SingleEnvelope<ConversationDetail>>(
      `/api/support/conversations/${id}/flag`,
      { method: "POST", body: { reason } },
    ),

  unflagConversation: (id: string | number) =>
    api<SingleEnvelope<ConversationDetail>>(
      `/api/support/conversations/${id}/flag`,
      { method: "DELETE" },
    ),

  escalateConversation: (id: string | number, body: EscalateRequest) =>
    api<EscalateResponse>(`/api/support/conversations/${id}/escalate`, {
      method: "POST",
      body,
    }),
};
